import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  DatePicker,
} from 'antd';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import Select from 'components/Select';
import { FilePdfOutlined } from '@ant-design/icons';
import { postObtenerReporte } from 'api/reporteador';
import Previewer from 'components/Previewer';
import { DATE_FRONTEND_FORMAT, dateFormatterToReporteador } from 'utils/formatValues';
import { getUnidadesDeRecaudacion } from 'api/configuracion/unidadesDeRecaudacion';
import moment from 'moment';
import axios from 'axios';
import getAllPages from 'utils/getAllPages';
import { getPlantillasDeTramitePortal } from 'api/tramites';

export const permissionConfig = {
  mainPermission: 'reportes_catastro',
  required: ['view_unidadderecaudacion', 'view_plantilladetramite'],
};

const data = [
  [130, 'REPORTE GENERAL DE TRÁMITES'],
  [30, 'DETALLADO DE TRÁMITES COBRADOS'],
].map(([id, nombre]) => ({ id, nombre }));

const ReportesDeCatastro = () => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visiblePreviewer, setVisiblePreviewer] = useState(false);
  const [base64, setBase64] = useState('');
  const [unidades, setUnidades] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [loadingPlantillas, setLoadingPlantillas] = useState(true);

  const [selectedRowKey] = selectedRowKeys;
  const nombreReporte = data.find((item) => item.id === selectedRowKey)?.nombre;

  useEffect(() => {
    let mounted = true;
    const source = axios.CancelToken.source();

    const fetchPlantillas = async () => {
      const _data = await getAllPages(getPlantillasDeTramitePortal, {}, source.token);
      if (mounted) {
        setPlantillas(_data);
        setLoadingPlantillas(false);
      }
    };

    fetchPlantillas();
    return () => {
      source.cancel();
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const _unidades = await getUnidadesDeRecaudacion();
      if (mounted) {
        setUnidades(_unidades);
        setLoading(false);
      }
    };

    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setBase64('');
  };

  const formatPostValues = (ID, values) => {
    const keys = Object.keys(values);
    const Parametros = keys.map((Nombre) => {
      const fields = {
        fechainicio: () => dateFormatterToReporteador(values[Nombre]),
        fechafinal: () => dateFormatterToReporteador(values[Nombre], false),
      };
      const dataType = {
        fechainicial: 'Fecha',
        fechafinal: 'Fecha',
        unidadrecaudacion: 'Entero',
        plantilla: 'Entero',
      };
      const Valor = (fields[Nombre] && fields[Nombre]()) || values[Nombre] || null;
      const TipoParametro = dataType?.[Nombre] || 'Texto';
      return {
        Nombre,
        TipoParametro,
        Valor,
      };
    }).filter((e) => e.Valor !== null);
    return { Parametros, ID };
  };

  const onFinish = async () => {
    setLoading(true);
    const _values = form.getFieldsValue();
    const [key] = selectedRowKeys;

    if (_values.fechas) {
      [_values.fechainicio, _values.fechafinal] = _values.fechas;
      delete _values.fechas;
    }
    const values = formatPostValues(key, _values);
    const args = [values];
    const result = await postObtenerReporte(...args);
    if (result) {
      onCancel();
      setBase64(result);
      setVisiblePreviewer(true);
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickConsultar = () => {
    form.resetFields();
    setVisible(true);
  };

  const rules = { required: [{ required: true }] };

  const columns = [
    {
      titleText: 'Nombre del reporte',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 350,
    },
  ];

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          customActions={[{
            icon: <FilePdfOutlined />,
            text: 'Consultar',
            onClick: onClickConsultar,
          }]}
          rowKey="id"
          mobileColIndex={0}
        />
        <Modal
          open={visible}
          footer={null}
          closable={false}
          maskClosable={false}
          centered
          width={550}
          title={(
            <FormSubmitControls
              onFinish={form.submit}
              onCancel={onCancel}
              loading={loading}
              label={nombreReporte}
              onFinishText="Ejecutar"
            />
          )}
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={() => onFinish()}
            scrollToFirstError
          >
            <Row gutter={10}>
              <Col span={24}>
                <Form.Item
                  label="Fecha Inicial y Final"
                  rules={rules.required}
                  name="fechas"
                >
                  <DatePicker.RangePicker
                    placeholder=""
                    format={DATE_FRONTEND_FORMAT}
                    disabledDate={(current) => current && current > moment().endOf('day')}
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
              {selectedRowKey === 130 && (
                <Col span={24}>
                  <Form.Item
                    label="Dependencia"
                    rules={rules.required}
                    name="unidadrecaudacion"
                  >
                    <Select dataSource={unidades} disabled={loading} />
                  </Form.Item>
                </Col>
              )}
              {selectedRowKey === 30 && (
                <Col span={24}>
                  <Form.Item
                    label="Plantilla"
                    rules={rules.required}
                    name="plantilla"
                  >
                    <Select
                      dataSource={plantillas}
                      loading={loadingPlantillas}
                      disabled={loading}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
      </Spin>
      <Previewer
        visible={visiblePreviewer}
        base64={base64}
        setVisible={(value) => {
          setVisiblePreviewer(value);
          onCancel();
        }}
        allowSend
        isImage={false}
        allowDownload
        fileName={nombreReporte}
        // _visibleInput
      />
    </Row>
  );
};

export default ReportesDeCatastro;
